import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMatches } from '../../hooks/useMatches' // Import hook for updates
import { useTeams } from '../../hooks/useTeams'
import { usePoules } from '../../hooks/usePoules'
import { Card, Button, Badge, DarkModeToggleCompact } from '../../components/ui'
import { HelpTip } from '../../components/ui/HelpTip'
import { ArrowLeft, Trophy, Medal, Rocket, GitBranch, Grid, Shield, Edit2, Save, X } from 'lucide-react'
import { supabase } from '../../lib/supabase'

import { useTranslation } from 'react-i18next'

export const StandingsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tournamentId } = useParams()
  // Destructure updateScore and updateMatch from hook
  const { matches, updateScore, updateMatch } = useMatches(tournamentId)

  // Derived Knockout State
  const knockoutMatches = matches.filter(m => !m.poule_id || ['quarter_final', 'semi_final', 'final', 'third_place'].includes(m.type))
  const quarters = knockoutMatches.filter(m => m.type === 'quarter_final')
  const semis = knockoutMatches.filter(m => m.type === 'semi_final')
  const finals = knockoutMatches.filter(m => m.type === 'final')
  const thirdPlace = knockoutMatches.find(m => m.type === 'third_place')
  const { teams } = useTeams(tournamentId)
  const { poules } = usePoules(tournamentId)

  const [pouleStandings, setPouleStandings] = useState({})
  const [generating, setGenerating] = useState(false)
  const [activeTab, setActiveTab] = useState('poules')

  // State for Knockout Modal
  const [showKoModal, setShowKoModal] = useState(false)
  const [advancingCount, setAdvancingCount] = useState(2) // Default top 2 advances

  // State for Score Editing
  const [editingMatchId, setEditingMatchId] = useState(null)

  useEffect(() => {
    calculateStandings()
  }, [matches, teams, poules])

  const calculateStandings = () => {
    const statsByPoule = {}
    teams.forEach(team => {
      const pId = team.poule_id || 'unknown'
      if (!statsByPoule[pId]) statsByPoule[pId] = []
      statsByPoule[pId].push({
        ...team,
        played: 0, won: 0, drawn: 0, lost: 0,
        goalsFor: 0, goalsAgainst: 0, goalDifference: 0, points: 0
      })
    })

    const isSetBased = matches.some(m => m.set_scores && m.set_scores.length > 0)

    matches.forEach(match => {
      // ONLY group stage matches count towards the group standings!
      if (match.type !== 'group') return
      if (match.score_home === null || match.score_away === null) return
      Object.keys(statsByPoule).forEach(pId => {
        const h = statsByPoule[pId].find(t => t.id === match.home_team_id)
        const a = statsByPoule[pId].find(t => t.id === match.away_team_id)

        if (h) {
          h.played++
          h.goalsFor += match.score_home
          h.goalsAgainst += match.score_away
          if (match.score_home > match.score_away) { h.won++; h.points += 3 }
          else if (match.score_home < match.score_away) h.lost++;
          else { h.drawn++; h.points += 1 }

          if (match.set_scores && Array.isArray(match.set_scores)) {
            match.set_scores.forEach(s => {
              h.gamesFor = (h.gamesFor || 0) + parseInt(s.home || 0)
              h.gamesAgainst = (h.gamesAgainst || 0) + parseInt(s.away || 0)
            })
          }
        }

        if (a) {
          a.played++
          a.goalsFor += match.score_away
          a.goalsAgainst += match.score_home
          if (match.score_away > match.score_home) { a.won++; a.points += 3 }
          else if (match.score_away < match.score_home) a.lost++;
          else { a.drawn++; a.points += 1 }

          if (match.set_scores && Array.isArray(match.set_scores)) {
            match.set_scores.forEach(s => {
              a.gamesFor = (a.gamesFor || 0) + parseInt(s.away || 0)
              a.gamesAgainst = (a.gamesAgainst || 0) + parseInt(s.home || 0)
            })
          }
        }
      })
    })

    Object.keys(statsByPoule).forEach(pId => {
      statsByPoule[pId].forEach(t => {
        // If games available, prefer games diff, otherwise goals diff
        t.goalDifference = (t.gamesFor !== undefined) ? (t.gamesFor - t.gamesAgainst) : (t.goalsFor - t.goalsAgainst)
        t.isSetBased = (t.gamesFor !== undefined)
      })
      statsByPoule[pId].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points
        if (a.isSetBased) {
          // 1. Sets difference (sets won - sets lost)
          const aSetsDiff = a.goalsFor - a.goalsAgainst
          const bSetsDiff = b.goalsFor - b.goalsAgainst
          if (bSetsDiff !== aSetsDiff) return bSetsDiff - aSetsDiff

          // 2. Games difference (games won - games lost)
          const aGamesDiff = (a.gamesFor || 0) - (a.gamesAgainst || 0)
          const bGamesDiff = (b.gamesFor || 0) - (b.gamesAgainst || 0)
          if (bGamesDiff !== aGamesDiff) return bGamesDiff - aGamesDiff

          // 3. Sets won
          if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor
        } else {
          // Standard goals-based sorting: 1. points, 2. GD, 3. GF
          if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
          if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor
        }
        return 0
      })
    })

    setPouleStandings(statsByPoule)
  }

  const handleGenerateClick = () => setShowKoModal(true)

  const generateKnockout = async () => {
    setGenerating(true)
    try {
      // First check if ANY knockout matches exist for this tournament
      const { data: existingKo } = await supabase
        .from('matches')
        .select('id')
        .eq('tournament_id', tournamentId)
        .in('type', ['quarter_final', 'semi_final', 'final', 'third_place'])

      if (existingKo && existingKo.length > 0) {
        const confirmOverWrite = window.confirm(
          "Er bestaan al knockout-wedstrijden voor dit toernooi. Wil je alle bestaande knockout-wedstrijden verwijderen en opnieuw genereren?"
        )
        if (!confirmOverWrite) {
          setGenerating(false)
          return
        }

        // Delete existing knockout matches
        const { error: deleteError } = await supabase
          .from('matches')
          .delete()
          .eq('tournament_id', tournamentId)
          .in('type', ['quarter_final', 'semi_final', 'final', 'third_place'])

        if (deleteError) throw deleteError
      }

      const pouleIds = Object.keys(pouleStandings).filter(id => id !== 'unknown')
      let matchesToCreate = []

      // --- STRATEGY SELECTION ---
      // We try standard logic first. If that fails (e.g. 3 poules), we switch to GLOBAL RANKING.
      // But we can also offer global ranking as the default for "Auto" mode if the user requests "Smart" generation.

      const MAX_TEAMS = advancingCount * (pouleIds.length || 1) // Rough check of total slots needed? 
      // Actually advancingCount is usually "Total knockout size" in the new modal? 
      // No, currently it's "Top X per poule". 
      // Let's change the interpretation: advancingCount = Target Bracket Size (2, 4, 8, 16)
      // This matches the "Global" approach better.

      // Let's Flatten and Rank ALL teams
      let allTeamsRanked = []
      pouleIds.forEach(pid => {
        allTeamsRanked.push(...pouleStandings[pid])
      })

      // Sort Global
      allTeamsRanked.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
        return b.goalsFor - a.goalsFor
      })

      console.log("Global Ranking:", allTeamsRanked.map(t => `${t.name} (${t.points}pts)`))

      // Take Top X
      // advancingCount from modal should be the TOTAL size (e.g. 8 for Quarters)
      // But existing modal sends 1, 2, 4 as "per poule"?
      // Let's adapt the modal later? Or just assume the User selected "Total Teams" if we go Global.
      // Actually, let's keep it simple: Use Global Ranking if poule logic fails OR if we want to force it.

      // We'll try specific logic first, fallback to Global.
      let usedGlobal = false

      if (pouleIds.length === 1 && [2, 4].includes(advancingCount)) {
        // Standard Single Poule Logic
        const poule = pouleStandings[pouleIds[0]]
        if (advancingCount === 2) {
          matchesToCreate.push({ tournament_id: tournamentId, type: 'final', round_name: t('standings_page.final_title'), home_team_id: poule[0]?.id, away_team_id: poule[1]?.id })
        } else {
          const sfLabel = t('standings_page.semi_final_label')
          matchesToCreate.push(
            { tournament_id: tournamentId, type: 'semi_final', round_name: `${sfLabel} 1`, home_team_id: poule[0]?.id, away_team_id: poule[3]?.id },
            { tournament_id: tournamentId, type: 'semi_final', round_name: `${sfLabel} 2`, home_team_id: poule[1]?.id, away_team_id: poule[2]?.id }
          )
        }
      }
      else if (pouleIds.length === 2 && [1, 2, 4].includes(advancingCount)) {
        // Standard 2 Poule Logic (Cross Finals)
        // Interpreting advancingCount as "Pairs to create" or "Teams per poule"? 
        // Previous code: advancingCount loops.
        const [pouleA, pouleB] = pouleIds.map(id => pouleStandings[id])
        let roundType = 'unknown'; let roundLabelBase = '';
        if (advancingCount === 1) { roundType = 'final'; roundLabelBase = t('standings_page.final_title'); } // 1 vs 1
        else if (advancingCount === 2) { roundType = 'semi_final'; roundLabelBase = t('standings_page.semi_final_label'); } // 1vs2, 2vs1
        else if (advancingCount === 4) { roundType = 'quarter_final'; roundLabelBase = t('standings_page.quarter_final_label'); } // 1..4 vs 4..1

        for (let i = 0; i < advancingCount; i++) {
          matchesToCreate.push({
            tournament_id: tournamentId, type: roundType, round_name: `${roundLabelBase} ${i + 1}`,
            home_team_id: pouleA[i]?.id, away_team_id: pouleB[advancingCount - 1 - i]?.id // Cross 1 vs 4
          })
        }
      }
      else if (pouleIds.length === 4 && advancingCount === 2) {
        // Standard 4 Poule Logic (Top 2 to Quarters)
        const [pA, pB, pC, pD] = pouleIds.map(id => pouleStandings[id])
        const qfLabel = t('standings_page.quarter_final_label')
        matchesToCreate.push(
          { tournament_id: tournamentId, type: 'quarter_final', round_name: `${qfLabel} 1`, home_team_id: pA[0]?.id, away_team_id: pB[1]?.id },
          { tournament_id: tournamentId, type: 'quarter_final', round_name: `${qfLabel} 2`, home_team_id: pB[0]?.id, away_team_id: pA[1]?.id },
          { tournament_id: tournamentId, type: 'quarter_final', round_name: `${qfLabel} 3`, home_team_id: pC[0]?.id, away_team_id: pD[1]?.id },
          { tournament_id: tournamentId, type: 'quarter_final', round_name: `${qfLabel} 4`, home_team_id: pD[0]?.id, away_team_id: pC[1]?.id }
        )
      }
      else {
        // --- GLOBAL FALLBACK ---
        usedGlobal = true
        // Logic: We take the top X teams globally.
        // X depends on advancingCount (which is messy now). 
        // Let's assume user wants a bracket of size 4, 8, or 16 based on available teams?
        // Fallback: If 3 poules * 4 teams = 12 teams. Top 8 go to Quarter Final.

        let targetSize = 4
        if (allTeamsRanked.length >= 8) targetSize = 8
        if (allTeamsRanked.length >= 16) targetSize = 16

        // Override target size with modal selection if we can map it?
        // Let's map modal input: 1->2(Final), 2->4(Semi), 4->8(Quarter)
        // The modal says "Top X". 
        if (advancingCount === 1) targetSize = 2 // Final
        if (advancingCount === 2) targetSize = 4 // Semis
        if (advancingCount === 4) targetSize = 8 // Quarters

        if (allTeamsRanked.length < targetSize) {
          throw new Error(t('standings_page.not_enough_teams', { count: allTeamsRanked.length, targetSize }))
        }

        const topTeams = allTeamsRanked.slice(0, targetSize)
        console.log(`Fallback Global: Taking top ${targetSize} teams for bracket`)

        // Create Matches (Seeded: 1 vs 8, 2 vs 7, etc)
        // Helper round name
        let rName = '', rType = ''
        if (targetSize === 2) { rName = t('standings_page.final_title'); rType = 'final' }
        else if (targetSize === 4) { rName = t('standings_page.semi_final_label'); rType = 'semi_final' }
        else if (targetSize === 8) { rName = t('standings_page.quarter_final_label'); rType = 'quarter_final' }
        else if (targetSize === 16) { rName = t('standings_page.achtste_finale'); rType = 'round_16' }

        for (let i = 0; i < targetSize / 2; i++) {
          matchesToCreate.push({
            tournament_id: tournamentId,
            type: rType,
            round_name: `${rName} ${i + 1}`,
            home_team_id: topTeams[i].id, // #1
            away_team_id: topTeams[targetSize - 1 - i].id, // #8
            match_index: i,
            // We can't easily predict next_match_index without a full tree generator logic here
            // But our Repair Button handles that now! :)
          })
        }
      }

      const { error } = await supabase.from('matches').insert(matchesToCreate)
      if (error) throw error

      setShowKoModal(false)
      setActiveTab('bracket') // Switch to bracket view
      alert(t('standings_page.generator_success', { count: matchesToCreate.length }) + (usedGlobal ? t('standings_page.global_rank_hint') : ""))

      window.location.reload()

    } catch (err) {
      alert(t('common.error') + ': ' + err.message)
    } finally {
      setGenerating(false)
    }
  }

  const promoteWinners = async (sourceRound) => {
    try {
      if (!confirm(t('standings_page.promote_confirm'))) return

      const sourceMatches = sourceRound === 'quarter_final' ? quarters : semis
      const winners = []
      const losers = []

      // Check if all finished
      const unfinished = sourceMatches.filter(m => m.score_home === null || m.score_away === null)
      if (unfinished.length > 0) throw new Error(t('standings_page.unfinished_matches_error'))

      const draws = sourceMatches.filter(m => Number(m.score_home) === Number(m.score_away))
      if (draws.length > 0) {
        const drawNames = draws.map(m => `${m.home_team?.name || 'Home'} vs ${m.away_team?.name || 'Away'}`).join('\n')

        // Detect if racket sports (sets logic used)
        const isSetBased = draws.some(m => m.set_scores && m.set_scores.length > 0)
        const helpText = isSetBased
          ? t('standings_page.draw_error_sets')
          : t('standings_page.draw_error_scores')

        throw new Error(t('standings_page.draw_detected_error', { matches: drawNames, help: helpText }))
      }

      sourceMatches.forEach(m => {
        if (Number(m.score_home) > Number(m.score_away)) {
          winners.push(m.home_team_id)
          losers.push(m.away_team_id)
        } else {
          winners.push(m.away_team_id)
          losers.push(m.home_team_id)
        }
      })

      const newMatches = []
      if (sourceRound === 'quarter_final') {
        // Create Semis (Winner Match 1 vs Winner Match 2)
        // Usually: Q1 vs Q2, Q3 vs Q4
        for (let i = 0; i < winners.length; i += 2) {
          newMatches.push({
            tournament_id: tournamentId,
            type: 'semi_final',
            round_name: `${t('standings_page.semi_final_label')} ${Math.floor(i / 2) + 1}`,
            home_team_id: winners[i],
            away_team_id: winners[i + 1],
            score_home: null, score_away: null
          })
        }
      } else if (sourceRound === 'semi_final') {
        // Create Final
        newMatches.push({
          tournament_id: tournamentId,
          type: 'final',
          round_name: t('standings_page.final_title'),
          home_team_id: winners[0],
          away_team_id: winners[1],
          score_home: null, score_away: null
        })
        // Create Third Place
        if (losers.length >= 2) {
          newMatches.push({
            tournament_id: tournamentId,
            type: 'third_place',
            round_name: t('standings_page.consolation_label'),
            home_team_id: losers[0],
            away_team_id: losers[1],
            score_home: null, score_away: null
          })
        }
      }

      // CHECK FOR EXISTING MATCHES TO PREVENT DUPLICATES
      // We check if matches of this type/round_name already exist for this tournament
      const { data: existingMatches } = await supabase
        .from('matches')
        .select('id, round_name')
        .eq('tournament_id', tournamentId)
        .in('round_name', newMatches.map(m => m.round_name))

      const uniqueMatches = newMatches.filter(nm =>
        !existingMatches.some(em => em.round_name === nm.round_name)
      )

      if (uniqueMatches.length === 0) {
        alert(t('standings_page.round_exists_error'))
        return
      }

      await supabase.from('matches').insert(uniqueMatches)
      alert(t('standings_page.round_created_success'))
      window.location.reload()

    } catch (err) {
      alert(t('common.error') + ': ' + err.message)
    }
  }

  // --- SCORE EDIT HANDLER ---
  const handleScoreUpdate = async (matchId, h, a) => {
    try {
      await updateScore(matchId, h, a)
      setEditingMatchId(null)
    } catch (error) {
      // Error handled by hook
      setEditingMatchId(null)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="premium-container">
        {/* Hero Section */}
        <div className="mb-12 animate-slideUp">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
            <ArrowLeft size={16} className="mr-2" />
            {t('standings_page.back_to_dashboard')}
          </Button>

          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg">
                <Trophy className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  {t('standings_page.title')}
                </h1>
                <p className="text-lg text-gray-600 mt-1">{t('standings_page.subtitle')}</p>
              </div>
            </div>

            <DarkModeToggleCompact />
          </div>
        </div>



        <HelpTip title={t('standings_page.auto_standings_title')}>
          <span dangerouslySetInnerHTML={{ __html: t('standings_page.auto_standings_text') }} />
        </HelpTip>

        {/* TABS - Premium Glass Style */}
        <div className="flex w-full mb-8 glass-strong p-1.5 rounded-2xl border border-white/30 shadow-lg">
          <button
            onClick={() => setActiveTab('poules')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'poules'
              ? 'bg-[var(--accent-primary)] text-white shadow-md'
              : 'text-gray-600 hover:bg-white/20'
              }`}
          >
            <Grid size={18} />
            {t('standings_page.tab_poules')}
          </button>
          <button
            onClick={() => setActiveTab('bracket')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'bracket'
              ? 'bg-[var(--accent-primary)] text-white shadow-md'
              : 'text-gray-600 hover:bg-white/20'
              }`}
          >
            <GitBranch size={18} />
            {t('standings_page.tab_bracket')}
          </button>
        </div>

        {activeTab === 'poules' && (
          <div className="animate-fade-in">
            {/* Action Bar for Poules */}
            <div className="flex justify-end mb-4">
              <Button
                onClick={handleGenerateClick}
                className="bg-[var(--accent-primary)] text-white shadow-lg hover:scale-105 transition-transform font-bold px-6"
              >
                <Rocket className="mr-2" size={18} />
                {t('standings_page.to_final_btn') || "Naar Finales 🏆"}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {poules.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm border border-gray-200">
                  {t('standings_page.no_poules')}
                </div>
              ) : poules.map(poule => {
                const standings = pouleStandings[poule.id] || []
                return (
                  <div key={poule.id} className="glass-strong premium-card rounded-2xl border border-white/30 shadow-lg overflow-hidden hover-lift hover:scale-[1.01] transition-all flex flex-col">
                    <div className="p-4 bg-gray-50/50 border-b border-white/10 flex justify-between items-center">
                      <h2 className="font-bold text-lg text-gray-900">{poule.name}</h2>
                    </div>

                    <div className="p-4 overflow-x-auto">
                      <table className="w-full min-w-[300px]">
                        <thead className="border-b border-white/10 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <tr>
                            <th className="pb-3 pl-2 w-8">{t('standings_page.table_header_rank')}</th>
                            <th className="pb-3">{t('standings_page.table_header_team')}</th>
                            <th className="pb-3 text-center w-8" title="Gespeeld">{t('presentation_page.tv_played', 'GS')}</th>
                            <th className="pb-3 text-center w-8">{t('standings_page.table_header_win')}</th>
                            {!standings[0]?.isSetBased && <th className="pb-3 text-center w-8">{t('standings_page.table_header_draw')}</th>}
                            <th className="pb-3 text-center w-8">{t('standings_page.table_header_loss')}</th>
                            <th className="pb-3 text-center w-8 text-gray-400" title={standings[0]?.isSetBased ? 'Games Saldo' : 'Doelsaldo'}>
                              {standings[0]?.isSetBased ? t('standings_page.table_header_games') : t('standings_page.table_header_goals')}
                            </th>
                            {standings[0]?.isSetBased && <th className="pb-3 text-center w-12 text-gray-400">{t('standings_page.table_header_sets')}</th>}
                            <th className="pb-3 text-center font-bold text-gray-700 w-10">{t('standings_page.table_header_points')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10 text-xs sm:text-sm">
                          {standings.map((team, index) => {
                            const isPromoting = index < advancingCount
                            return (
                              <tr key={team.id} className={`hover:bg-white/5 transition-colors ${isPromoting ? 'bg-[rgba(34,197,94,0.05)]' : ''}`}>
                                <td className="py-3 pl-2">
                                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold ${
                                    index === 0 ? 'bg-yellow-400 text-yellow-900 shadow-sm' :
                                    index === 1 ? 'bg-gray-300 text-gray-700' :
                                    'bg-white/10 text-gray-400'
                                  }`}>
                                    {index + 1}
                                  </div>
                                </td>
                                <td className="py-3">
                                  <div className="flex items-center gap-2">
                                    {team.logo_url && (
                                      <img src={team.logo_url} className="w-6 h-6 object-contain rounded p-0.5 bg-white border border-gray-100 shadow-sm" alt="" />
                                    )}
                                    <span className="font-semibold text-gray-900 block truncate max-w-[120px] sm:max-w-none">{team.name}</span>
                                    {isPromoting && (
                                      <span className="ml-1 text-[10px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full hidden sm:inline">
                                        {t('standings_page.promote_badge', '✓')}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 text-center text-gray-700 font-mono">{team.played}</td>
                                <td className="py-3 text-center text-gray-700">{team.won}</td>
                                {!team.isSetBased && <td className="py-3 text-center text-gray-700">{team.drawn}</td>}
                                <td className="py-3 text-center text-gray-700">{team.lost}</td>
                                <td className="py-3 text-center text-gray-400 font-mono">{team.goalDifference > 0 ? `+${team.goalDifference}` : team.goalDifference}</td>
                                {team.isSetBased && (
                                  <td className="py-3 text-center text-xs text-gray-500">
                                    {team.goalsFor}-{team.goalsAgainst}
                                  </td>
                                )}
                                <td className="py-3 text-center font-bold text-gray-900">{team.points}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {activeTab === 'bracket' && (
          <div className="animate-fade-in">
            {knockoutMatches.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-24 glass-strong premium-card rounded-3xl border border-white/20 shadow-xl max-w-2xl mx-auto mt-6">
                <div className="p-6 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-full mb-6 text-[var(--accent-primary)] border border-white/20 shadow-inner">
                  <Shield size={64} className="animate-pulse" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('standings_page.no_finals_title')}</h2>
                <p className="text-gray-500 max-w-md mb-8 text-sm leading-relaxed">
                  {t('standings_page.no_finals_text')}
                </p>
                <Button
                  onClick={handleGenerateClick}
                  className="bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white hover:opacity-95 shadow-lg shadow-purple-500/20 py-4 px-8 rounded-xl text-lg font-bold"
                >
                  <Rocket className="mr-2" />
                  {t('standings_page.generate_finals_btn')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-12 justify-center items-start overflow-x-auto pb-12 pt-8">
                {/* BRACKET COLUMNS */}
                {/* QUARTERS */}
                {quarters.length > 0 && (
                  <div className="flex-1 min-w-[320px] max-w-[400px] space-y-8 relative">
                    <div className="glass-strong text-gray-800 text-center py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs border border-white/25 shadow-md">
                      {t('standings_page.quarters_title')}
                    </div>
                    <div className="space-y-8 relative">
                      {quarters.map(match => (
                        <div key={match.id} className="relative">
                          <MatchCard
                            match={match}
                            isEditing={editingMatchId === match.id}
                            onEdit={() => setEditingMatchId(match.id)}
                            onCancel={() => setEditingMatchId(null)}
                            onSave={handleScoreUpdate}
                          />
                          {/* Connector Line Right */}
                          <div className="hidden md:block absolute top-1/2 -right-6 w-6 h-0.5 bg-gray-300/40"></div>
                        </div>
                      ))}
                    </div>
                    {semis.length === 0 && (
                      <Button onClick={() => promoteWinners('quarter_final')} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/10">
                        {t('standings_page.generate_semis_btn')}
                      </Button>
                    )}
                  </div>
                )}

                {/* SEMIS */}
                {semis.length > 0 && (
                  <div className={`flex-1 min-w-[320px] max-w-[400px] space-y-8 relative ${quarters.length > 0 ? 'md:mt-16' : ''}`}>
                    <div className="glass-strong text-gray-800 text-center py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs border border-white/25 shadow-md">
                      {t('standings_page.semis_title')}
                    </div>
                    <div className="space-y-24 relative"> {/* Increased spacing for tree effect */}
                      {semis.map(match => (
                        <div key={match.id} className="relative">
                          {/* Connector Line Left (Vertical + Horizontal) - Simplified for now */}
                          <div className="hidden md:block absolute top-1/2 -left-6 w-6 h-0.5 bg-gray-300/40"></div>

                          <MatchCard
                            key={match.id}
                            match={match}
                            isEditing={editingMatchId === match.id}
                            onEdit={() => setEditingMatchId(match.id)}
                            onCancel={() => setEditingMatchId(null)}
                            onSave={handleScoreUpdate}
                          />
                          {/* Connector Line Right */}
                          <div className="hidden md:block absolute top-1/2 -right-6 w-6 h-0.5 bg-gray-300/40"></div>
                        </div>
                      ))}
                    </div>
                    {finals.length === 0 && (
                      <Button onClick={() => promoteWinners('semi_final')} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/10">
                        {t('standings_page.to_final_btn')}
                      </Button>
                    )}
                  </div>
                )}

                {/* FINALS */}
                {finals.length > 0 && (
                  <div className={`flex-1 min-w-[320px] max-w-[400px] space-y-8 ${semis.length > 0 ? 'md:mt-32' : ''}`}>
                    <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 text-amber-700 text-center py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs border border-yellow-500/30 shadow-md flex justify-center items-center gap-2">
                      <Trophy size={16} /> {t('standings_page.final_title')}
                    </div>
                    {finals.map(match => (
                      <div key={match.id} className="relative">
                        <div className="hidden md:block absolute top-1/2 -left-6 w-6 h-0.5 bg-gray-300/40"></div>
                        <MatchCard
                          match={match}
                          isFinal
                          isEditing={editingMatchId === match.id}
                          onEdit={() => setEditingMatchId(match.id)}
                          onCancel={() => setEditingMatchId(null)}
                          onSave={handleScoreUpdate}
                        />
                      </div>
                    ))}
                    {thirdPlace && (
                      <div className="mt-12 opacity-90">
                        <div className="text-center text-xs font-bold text-gray-500 mb-2 uppercase">{t('standings_page.consolation_final')}</div>
                        <MatchCard
                          match={thirdPlace}
                          isEditing={editingMatchId === thirdPlace.id}
                          onEdit={() => setEditingMatchId(thirdPlace.id)}
                          onCancel={() => setEditingMatchId(null)}
                          onSave={handleScoreUpdate}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* MODAL */}
        {showKoModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in p-4">
            <div className="glass-strong rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/30 border-t-white/50 animate-scaleIn">
              <h2 className="text-2xl font-bold mb-2 text-gray-900">{t('standings_page.modal_title')}</h2>
              <p className="text-gray-500 mb-6 text-sm leading-relaxed">{t('standings_page.modal_desc')}</p>

              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: t('standings_page.advancing_label') }} />
                <select
                  value={advancingCount}
                  onChange={(e) => setAdvancingCount(parseInt(e.target.value))}
                  className="w-full p-4 rounded-xl border border-white/30 focus:border-[var(--accent-primary)] focus:ring-4 focus:ring-[var(--accent-primary)]/20 outline-none text-lg font-bold bg-white/5 text-gray-800"
                >
                  <option value="1" className="bg-white text-gray-900">{t('standings_page.opt_top1')}</option>
                  <option value="2" className="bg-white text-gray-900">{t('standings_page.opt_top2')}</option>
                  <option value="4" className="bg-white text-gray-900">{t('standings_page.opt_top4')}</option>
                </select>
              </div>

              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => setShowKoModal(false)} className="flex-1">{t('common.cancel')}</Button>
                <Button onClick={generateKnockout} disabled={generating} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20">
                  {generating ? t('standings_page.creating') : t('standings_page.generate_btn')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Standard MatchCard (Premium Glass Look)
const MatchCard = ({ match, isFinal, isEditing, onEdit, onCancel, onSave }) => {
  const scoreA = match.score_home
  const scoreB = match.score_away
  const hasScore = scoreA !== null && scoreB !== null

  const winA = hasScore && Number(scoreA) > Number(scoreB)
  const winB = hasScore && Number(scoreB) > Number(scoreA)
  const draw = hasScore && Number(scoreA) === Number(scoreB)

  // Color logic for score boxes - Premium HSL Hues
  const getScoreColor = (isWinner, isLoser) => {
    if (!hasScore) return 'bg-white/5 text-gray-400 border border-white/10'
    if (isWinner) return 'bg-green-500/20 text-green-700 border border-green-500/30' // Premium Glass Green
    if (isLoser) return 'bg-red-500/10 text-red-600 border border-red-500/10' // Premium Glass Red
    return 'bg-amber-500/10 text-amber-600 border border-amber-500/20' // Premium Glass Amber
  }

  return (
    <div className={`glass-subtle rounded-2xl shadow-md border border-white/20 p-0 overflow-hidden hover:scale-[1.02] transition-all hover:shadow-lg duration-200 ${isFinal ? 'ring-2 ring-yellow-400/80 shadow-yellow-500/10' : ''}`}>
      {/* Header */}
      <div className="bg-white/5 px-3 py-2 border-b border-white/10 flex justify-between items-center">
        <span className="text-[10px] font-extrabold text-gray-500 uppercase tracking-widest">{match.round_name}</span>
        {!isEditing && (
          <button onClick={onEdit} className="p-1 text-gray-400 hover:text-[var(--accent-primary)] hover:bg-white/10 rounded-lg transition-colors">
            <Edit2 size={12} />
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Team 1 */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 overflow-hidden flex-1 mr-2">
            {match.home_team?.logo_url ? (
              <img src={match.home_team.logo_url} className="w-6 h-6 object-contain rounded p-0.5 bg-white border border-gray-100 shadow-sm" alt="" />
            ) : (
              <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center text-[10px] font-bold text-gray-400 border border-white/10">?</div>
            )}
            <span className={`text-sm truncate font-semibold ${winA ? 'text-gray-900 font-extrabold' : 'text-gray-600'}`}>
              {match.home_team?.name || (match.home_team_id ? 'Team 1' : '...')}
            </span>
          </div>

          {isEditing ? (
            <input
              id={`inline-score-home-${match.id}`}
              type="number"
              defaultValue={match.score_home}
              className="w-10 text-center bg-white/5 border border-white/20 focus:border-[var(--accent-primary)] rounded-lg text-sm py-1 font-bold outline-none text-gray-800"
              autoFocus
            />
          ) : (
            <div className={`w-8 h-6 flex items-center justify-center rounded-lg text-xs font-bold ${getScoreColor(winA, winB)}`}>
              {scoreA ?? '-'}
            </div>
          )}
        </div>

        {/* Team 2 */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 overflow-hidden flex-1 mr-2">
            {match.away_team?.logo_url ? (
              <img src={match.away_team.logo_url} className="w-6 h-6 object-contain rounded p-0.5 bg-white border border-gray-100 shadow-sm" alt="" />
            ) : (
              <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center text-[10px] font-bold text-gray-400 border border-white/10">?</div>
            )}
            <span className={`text-sm truncate font-semibold ${winB ? 'text-gray-900 font-extrabold' : 'text-gray-600'}`}>
              {match.away_team?.name || (match.away_team_id ? 'Team 2' : '...')}
            </span>
          </div>

          {isEditing ? (
            <input
              id={`inline-score-away-${match.id}`}
              type="number"
              defaultValue={match.score_away}
              className="w-10 text-center bg-white/5 border border-white/20 focus:border-[var(--accent-primary)] rounded-lg text-sm py-1 font-bold outline-none text-gray-800"
            />
          ) : (
            <div className={`w-8 h-6 flex items-center justify-center rounded-lg text-xs font-bold ${getScoreColor(winB, winA)}`}>
              {scoreB ?? '-'}
            </div>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="bg-white/5 px-3 py-2 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700 px-2 font-medium">{t('common.cancel')}</button>
          <button onClick={() => {
            const h = document.getElementById(`inline-score-home-${match.id}`).value
            const a = document.getElementById(`inline-score-away-${match.id}`).value
            onSave(match.id, h, a)
          }} className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg hover:opacity-95 font-bold shadow-md shadow-green-500/10">{t('common.save')}</button>
        </div>
      )}
    </div>
  )
}
